import { Icon } from '@modulor/react';
import { RECENT_GAMES_CATEGORY } from '../../../redux/constants/categories';
import {
  BONUS_HISTORY,
  BONUS_PAGE,
  BONUSES,
  BONUSES_COLLECTING_GAMES,
  BONUSES_WAGERING_GAMES,
  BONUSES_WAGERING_GAMES_SCCOM,
  CATEGORY_DETAIL,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
  PARAM_NAME,
  ROOT,
  RULES,
  SEARCH,
  SPECIAL_OFFERS,
  TOURNAMENTS,
} from '../../../constants/routeTemplates';
import { ANALYTICS_SCREEN_AREA_DETAILS } from '../../../constants/analytics';
import type { MainNavigationItem, MainNavigationPredicates } from './types';
import BonusesNotification from './components/BonusesNotification';
import { getPromoBonusUrl } from '../../../hooks/usePromoBonusSystemRedirects';
import { PROMO } from '../../../constants/productTypes';

export const navigationItems: MainNavigationItem[] = [
  {
    translationKey: 'LOBBY',
    linkPathBuilder: () => ROOT,
    Icon: (props = {}) => <Icon name="home_outlined" {...props} />,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.MAIN_PAGE,
    },
  },
  {
    Icon: (props = {}) => <Icon name="search" {...props} />,
    translationKey: 'SEARCH',
    linkPathBuilder: () => SEARCH,
    analyticsData: { analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.SEARCH },
    predicateResolver: ({ isDesktop }: MainNavigationPredicates) => {
      return isDesktop;
    },
  },
  {
    Icon: (props = {}) => <Icon name="gift_outlined" {...props} />,
    id: BONUSES,
    translationKey: 'BONUSES',
    linkPathBuilder: () => BONUSES,
    analyticsData: { analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.BONUS },
    activeRoutes: [BONUS_HISTORY, BONUS_PAGE],
    rightCellContent: <BonusesNotification />,
    predicateResolver: ({
      hasBonuses,
      isUnifiedPromoBonusSystemEnabled,
    }: MainNavigationPredicates) => {
      return hasBonuses && !isUnifiedPromoBonusSystemEnabled;
    },
  },
  // unified bonus system item
  {
    Icon: (props = {}) => <Icon name="gift_outlined" {...props} />,
    id: BONUSES,
    translationKey: 'BONUSES',
    linkPathBuilder: () => getPromoBonusUrl(),
    productType: PROMO,
    analyticsData: { analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.BONUS },
    activeRoutes: [BONUS_HISTORY, BONUS_PAGE],
    rightCellContent: <BonusesNotification />,
    predicateResolver: ({
      hasBonuses,
      isUnifiedPromoBonusSystemEnabled,
    }: MainNavigationPredicates) => {
      return hasBonuses && isUnifiedPromoBonusSystemEnabled;
    },
  },
  {
    Icon: (props = {}) => <Icon name="cup_outlined" {...props} />,
    id: TOURNAMENTS,
    translationKey: 'Tournaments',
    linkPathBuilder: () => TOURNAMENTS,
    predicateResolver: ({
      isTournamentLobbyAvailable,
    }: MainNavigationPredicates) => {
      return isTournamentLobbyAvailable;
    },
  },
  {
    Icon: (props = {}) => <Icon name="hexagram_outlined" {...props} />,
    translationKey: 'SPECIAL_OFFERS',
    linkPathBuilder: () => SPECIAL_OFFERS,
    analyticsData: { analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.PROMO },
    predicateResolver: ({ hasSpecialOffers }: MainNavigationPredicates) => {
      return hasSpecialOffers;
    },
  },
  {
    Icon: (props = {}) => <Icon name="cashback" {...props} />,
    translationKey: 'BONUSES_COLLECTING_GAMES',
    linkPathBuilder: () => BONUSES_COLLECTING_GAMES,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.COLLECTING_GAMES,
    },
    predicateResolver: ({
      isLoggedIn,
      hasBonuses,
      hasBonusCollectingGames,
    }: MainNavigationPredicates) => {
      return isLoggedIn && hasBonuses && hasBonusCollectingGames;
    },
  },
  {
    Icon: (props = {}) => <Icon name="wager" {...props} />,
    translationKey: 'BONUSES_WAGERING_GAMES',
    linkPathBuilder: () => BONUSES_WAGERING_GAMES,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.WAGERING_GAMES,
    },
    predicateResolver: ({
      isLoggedIn,
      hasBonuses,
      hasBonusWageringGames,
      isSCCOM,
    }: MainNavigationPredicates) => {
      return isLoggedIn && hasBonuses && hasBonusWageringGames && !isSCCOM;
    },
  },
  {
    Icon: (props = {}) => <Icon name="wager" {...props} />,
    translationKey: 'BONUSES_WAGERING_GAMES',
    linkPathBuilder: () => BONUSES_WAGERING_GAMES_SCCOM,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.WAGERING_GAMES,
    },
    predicateResolver: ({
      isLoggedIn,
      hasBonuses,
      hasBonusWageringGames,
      isSCCOM,
    }: MainNavigationPredicates) => {
      return isLoggedIn && hasBonuses && hasBonusWageringGames && isSCCOM;
    },
  },
  {
    translationKey: 'RULES',
    linkPathBuilder: () => RULES,
    Icon: (props = {}) => <Icon name="question_outlined" {...props} />,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.HOW_TO_PLAY,
    },
    predicateResolver: ({ areRulesEnabled }: MainNavigationPredicates) => {
      return areRulesEnabled;
    },
  },
  {
    translationKey: 'HALL_OF_FAME',
    linkPathBuilder: () => HALL_OF_FAME,
    Icon: (props = {}) => <Icon name="hall_of_fame_outlined" {...props} />,
    analyticsData: {
      analyticAreaDetail: ANALYTICS_SCREEN_AREA_DETAILS.HALL_OF_FAME,
    },
    activeRoutes: [HALL_OF_FAME, HALL_OF_FAME_HISTORY],
    predicateResolver: ({ hasHallOfFame }: MainNavigationPredicates) => {
      return hasHallOfFame;
    },
  },
  {
    Icon: (props = {}) => <Icon name="gift_outlined" {...props} />,
    translationKey: 'Recent games',
    linkPathBuilder: () =>
      CATEGORY_DETAIL.replace(`:${PARAM_NAME.CATEGORY}`, RECENT_GAMES_CATEGORY),
    predicateResolver: ({ isSCCOM }: MainNavigationPredicates) => {
      return isSCCOM;
    },
  },
];
